<script setup lang="ts">
// The map's finder, as one pane of the right-hand tool rail. It owns the input,
// the ledger tables' record queries and the shared address query; it owns no
// map side effect and writes neither `spec.filters` nor `spec.search`. A find is
// a find: picking a result reports it upward, and the view flies the map and
// opens the record or the Site popup.
//
// The groups read in a fixed order — the nine ledger tables in tab order, then
// the address tiers (situs above geocode) — so a term that matches both a Site
// and an address always shows the record first. The address rows carry the
// "Filter sites at this address" secondary action, the direct search → narrow
// bridge; it reports the address upward and the view writes the Site clause.
import { computed, ref, watch } from "vue";

import SearchInput from "@/components/search/SearchInput.vue";
import SearchResults from "@/components/search/SearchResults.vue";
import type { SearchGroup, SearchResult } from "@/components/search/types";
import { useAddressSearch } from "@/components/search/useAddressSearch";
import { useDebouncedTerm } from "@/components/search/useDebouncedTerm";
import type { AddressSearchResult } from "@/type/api";

import type { RecordSearchResult, SearchSelection } from "./searchResults";
import { useMapReviewSearch } from "./useMapReviewSearch";

const emit = defineEmits<{
	select: [selection: SearchSelection];
	"filter-address": [address: string];
	error: [error: { title: string; message: string }];
}>();

// 250ms and two characters: the same debounce the finder always had, so a query
// is issued once the operator pauses rather than on every keystroke, and a
// single character never leaves the pane.
const query = ref("");
const term = useDebouncedTerm(query, { delay: 250, minimumLength: 2 });

const { groups: recordGroups, error: recordError } = useMapReviewSearch(term);
const { groups: addressGroupList, error: addressError } =
	useAddressSearch(term);

/** A record payload, told apart from an address payload by the row it carries.
 * The two result kinds are the only payloads this pane ever builds, so the
 * guard is the whole dispatch. */
function isRecordResult(payload: unknown): payload is RecordSearchResult {
	return (
		typeof payload === "object" &&
		payload !== null &&
		"table" in payload &&
		"row" in payload
	);
}

// The groups in render order: the record tables first, then the address tiers
// in the order the shared address groups put them. The address rows gain the
// secondary action here, because the shared widget builds a row without one.
const groups = computed<SearchGroup[]>(() => [
	...recordGroups.value,
	...addressGroupList.value.map((group) => ({
		...group,
		results: group.results.map((result) => ({
			...result,
			secondaryAction: {
				label: "Filter sites at this address",
				icon: "bi bi-funnel",
			},
		})),
	})),
]);

/** Report a picked result upward: a record as the table/id/point/row the view
 * flies and opens, an address as the result the view resolves to a Site or a
 * pin. */
function onSelect(result: SearchResult) {
	const payload = result.payload;
	if (isRecordResult(payload)) {
		emit("select", {
			kind: "record",
			table: payload.table,
			id: payload.id,
			anchor: payload.anchor,
			row: payload.row,
		});
		return;
	}
	emit("select", { kind: "address", result: payload as AddressSearchResult });
}

/** The address row's secondary action: hand the view the address string to
 * narrow Sites by. A geocoded suggestion carries its address in `detail`
 * rather than `address`, and a result with none of them has nothing to filter
 * by, so it emits nothing. */
function onSecondary(result: SearchResult) {
	const payload = result.payload;
	if (isRecordResult(payload)) return;
	const address = payload as AddressSearchResult;
	const text = address.address || address.detail || address.locality || "";
	if (text) emit("filter-address", text);
}

// A failed search is the view's toast to show. Per-table failures stay inline
// as that group's own failure line; these two are the round-level ones — the
// record round only reports when every table failed at once, and the address
// query reports its own failure.
watch(recordError, (message) => {
	if (message) emit("error", { title: "Search failed", message });
});
watch(addressError, (message) => {
	if (message) emit("error", { title: "Search failed", message });
});
</script>

<template>
	<div class="control-pane control-pane--search">
		<SearchInput
			v-model="query"
			placeholder="Search records or address…"
			aria-label="Search records or address"
			autofocus
		/>
		<!-- The results take the height the box leaves, scrolling in place, so a
		     broad term scrolls the list rather than pushing the box off the top
		     of the pane. The shared list renders nothing for empty groups. -->
		<div v-if="groups.length > 0" class="search-pane-results">
			<SearchResults
				:groups="groups"
				@select="onSelect"
				@secondary="onSecondary"
			/>
		</div>
		<p
			v-else-if="term !== '' && !recordError && !addressError"
			class="search-pane-empty"
		>
			No matches for “{{ term }}”.
		</p>
	</div>
</template>

<style scoped>
/* The rail body owns the box — its width, background, padding and scroll — so
   the pane is only its content and its type scale, and the finder takes the
   whole of the body: the box at the top, its results under it. */
.control-pane {
	font-size: 0.8125rem;
}

.control-pane--search {
	display: flex;
	flex-direction: column;
	height: 100%;
}

.search-pane-results {
	flex: 1 1 auto;
	min-height: 0;
	margin-top: 4px;
	overflow-y: auto;
}

.search-pane-empty {
	margin: 4px 0 0;
	padding: 4px 8px;
	color: #6c757d;
	font-size: 0.75rem;
}
</style>
